import { BaseClient } from "../../clients/base/BaseClient";
import { LIS_ENDPOINTS } from "./lis.consts";
import type {
  VisitResponse,
  PatientHistoryResponse,
  FlaggingRequest,
  FlaggingResponse,
} from "./lis.types";

/**
 * LIS API Client
 * Handles all Laboratory Information System bridge operations.
 */
export class LisClient extends BaseClient {
  /**
   * Fetch visit information by sample barcode.
   * @param {string} sampleBarcode - Sample barcode.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<VisitResponse>} Visit information.
   */
  async fetchVisitBySample(
    sampleBarcode: string,
    signal?: AbortSignal | null,
  ): Promise<VisitResponse> {
    return this.get<VisitResponse>(LIS_ENDPOINTS.SAMPLE_VISIT(sampleBarcode), {
      signal,
    });
  }

  /**
   * Fetch patient test history.
   * @param {string} sampleBarcode - Sample barcode.
   * @param {string} testId - Test ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<PatientHistoryResponse>} Array of historical test results.
   */
  async fetchPatientHistory(
    sampleBarcode: string,
    testId: string,
    signal?: AbortSignal | null,
  ): Promise<PatientHistoryResponse> {
    return this.get<PatientHistoryResponse>(LIS_ENDPOINTS.PATIENT_HISTORY(sampleBarcode, testId), {
      signal,
    });
  }

  /**
   * Calculate flagging for test results.
   * @param {FlaggingRequest} data - Array of test results to evaluate.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<FlaggingResponse>} Array of flagging results.
   */
  async calculateFlagging(
    data: FlaggingRequest,
    signal?: AbortSignal | null,
  ): Promise<FlaggingResponse> {
    return this.post<FlaggingResponse>(LIS_ENDPOINTS.FLAGGING, {
      body: data,
      signal,
    });
  }
}
